import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import Switch from '@mui/material/Switch';
import SidePanel from './util/SidePanel';
import Tooltip from '@mui/material/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import TabMenu from './util/TabMenu';
import PageToolbar from './util/PageToolbar';
import EmployeesTab from './hrComponents/EmployeesTab';
import HrOverview from './hrComponents/HrOverview';
import HrAbsenceWorkflow from './hrComponents/HrAbsenceWorkflow';
import HrLeaveBalanceTab from './hrComponents/HrLeaveBalanceTab';
import HrSettingsDrawer from './hrComponents/HrSettingsDrawer';
import SettingsPanelBody from './util/SettingsPanelBody';
import OrgTree, { CardsView, UnassignedBar } from './usersComponents/OrgTree';

interface HrPageProps {
  access: any;
  baseUrl: string;
  refreshUserHeder: any;
  refreshRoleAccept: any;
  staticDateCompany?: string;
}

const HrPage: React.FC<HrPageProps> = ({ access, baseUrl, refreshUserHeder, refreshRoleAccept, staticDateCompany }) => {
  // ?tab=requests (dashboard "Open Absence") opens the requests tab when allowed
  const [tab, setTab] = useState(() => {
    const canRequests = [access?.hr_absence_view, access?.hr_absence_view_sv, access?.hr_absence_view_team, access?.hr_absence_view_all].some(v => v === true);
    return new URLSearchParams(window.location.search).get('tab') === 'requests' && canRequests ? 'requests' : 'overview';
  });
  const [showSettings, setShowSettings] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [usersNotActive, setUsersNotActive] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [deptMemberships, setDeptMemberships] = useState<any[]>([]);
  const [deptEditMode, setDeptEditMode] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [usersRes, deptsRes, memRes, inactiveRes] = await Promise.allSettled([
        axios.get('users'),
        axios.get('department'),
        axios.get('hr-user-departments'),
        axios.get('users/inactive'),
      ]);
      if (usersRes.status === 'fulfilled') setUsers(Array.isArray(usersRes.value.data) ? usersRes.value.data : []);
      if (deptsRes.status === 'fulfilled') setDepartments(Array.isArray(deptsRes.value.data) ? deptsRes.value.data : []);
      if (memRes.status === 'fulfilled') setDeptMemberships(Array.isArray(memRes.value.data) ? memRes.value.data : []);
      if (inactiveRes.status === 'fulfilled') setUsersNotActive(Array.isArray(inactiveRes.value.data) ? inactiveRes.value.data : []);
    } catch {}
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const canViewRequests = access?.hr_absence_view === true || access?.hr_absence_view_sv === true
    || access?.hr_absence_view_team === true || access?.hr_absence_view_all === true;
  const canViewLeave = access?.hr_leave_view_own === true || access?.hr_leave_view_sv === true
    || access?.hr_leave_view_dep === true || access?.hr_leave_view_all === true
    || access?.hr_absence_view_all === true || access?.hr_absence_view_team === true;
  const canViewEmployees = access?.hr_employees_enabled === true
    || access?.users_view === true || access?.users_view_sub === true || access?.users_view_all === true
    || access?.hr_manage === true || access?.hr_admin === true;
  const canViewDept = access?.hr_org_enabled === true || access?.hr_manage === true || access?.hr_admin === true;
  const canViewOrgChart = access?.hr_org_chart_enabled === true || access?.hr_org_enabled === true || access?.hr_manage === true || access?.hr_admin === true;
  const canManage = access?.hr_manage === true || access?.hr_manage === 1;
  const canOrgEdit = access?.hr_org_edit === true || access?.hr_manage === true || access?.hr_admin === true;

  if (!access?.hr_enabled) {
    return <div style={{ padding: 32, color: 'var(--text-secondary)' }}>No access</div>;
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TabMenu
        value={tab}
        onChange={setTab}
        tabs={[
          { label: t('HrCalendar') || 'Calendar', value: 'overview' },
          { label: t('HrRequests'), value: 'requests', hidden: !canViewRequests },
          { label: t('HrLeaveBalance'), value: 'leave', hidden: !canViewLeave },
          { label: t('Employees') || 'Employees', value: 'employees', hidden: !canViewEmployees },
          { label: t('OrgChartDepartments') || 'Departments', value: 'dept', hidden: !canViewDept },
          { label: t('OrgChart') || 'Org Chart', value: 'tree', hidden: !canViewOrgChart },
        ]}
        rightElement={
          (access?.hr_settings === true || access?.hr_settings === 1) ? (
            <div className="flex items-center mr-[4px]">
              <Tooltip title={t('Settings')} placement="left">
                <FontAwesomeIcon
                  icon={faGear}
                  className="transition-transform duration-500 cursor-pointer opacity-60 hover:rotate-180 hover:opacity-90 text-[var(--text-color)]"
                  onClick={() => setShowSettings(true)}
                />
              </Tooltip>
            </div>
          ) : undefined
        }
      >
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {tab === 'overview' && <HrOverview users={users} access={access} baseUrl={baseUrl} />}
          {tab === 'requests' && <HrAbsenceWorkflow access={access} users={users} baseUrl={baseUrl} staticDateCompany={staticDateCompany} />}
          {tab === 'leave' && <HrLeaveBalanceTab access={access} users={users} departments={departments} baseUrl={baseUrl} staticDateCompany={staticDateCompany} />}
          {tab === 'employees' && (
            <EmployeesTab
              users={[...users, ...usersNotActive]}
              departments={departments}
              deptMemberships={deptMemberships}
              baseUrl={baseUrl}
              access={access}
              onRefresh={loadData}
            />
          )}
          {tab === 'dept' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-l1)' }}>
              <PageToolbar>
                <div style={{ flex: 1 }} />
                {canOrgEdit && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, color: deptEditMode ? '#4f8ef7' : 'var(--text-color-calendar-week)', fontWeight: deptEditMode ? 700 : 400, transition: 'color .2s' }}>
                      {t('EditMode')}
                    </span>
                    <Switch
                      size="small"
                      checked={deptEditMode}
                      onChange={(_, v) => setDeptEditMode(v)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4f8ef7' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4f8ef7' } }}
                    />
                  </div>
                )}
              </PageToolbar>
              <div className="ts-card" style={{ padding: 'var(--space-l2)' }}>
                <CardsView
                  users={users}
                  departments={departments}
                  baseUrl={baseUrl}
                  editMode={deptEditMode}
                  onUserDrop={async (userId, newDeptId) => {
                    try {
                      const mems = deptMemberships.filter((m: any) => m.userId === userId);
                      await Promise.all(mems.map((m: any) => axios.delete(`hr-user-departments/${userId}/${m.departmentId}`)));
                      if (newDeptId !== null) {
                        await axios.post('hr-user-departments/add', { userId, departmentId: newDeptId, role: 'member' });
                      }
                      loadData();
                    } catch {}
                  }}
                />
                <UnassignedBar
                  users={users.filter((u: any) => !u.departmentId)}
                  baseUrl={baseUrl}
                  editMode={deptEditMode}
                />
              </div>
            </div>
          )}
          {tab === 'tree' && (
            <OrgTree
              forcedView="tree"
              users={users}
              departments={departments}
              deptMemberships={deptMemberships}
              baseUrl={baseUrl}
              canManage={canOrgEdit}
              onUserClick={() => {}}
              onUserDrop={async (userId, newDeptId) => {
                try {
                  const mems = deptMemberships.filter((m: any) => m.userId === userId);
                  await Promise.all(mems.map((m: any) => axios.delete(`hr-user-departments/${userId}/${m.departmentId}`)));
                  if (newDeptId !== null) {
                    await axios.post('hr-user-departments/add', { userId, departmentId: newDeptId, role: 'member' });
                  }
                  loadData();
                } catch {}
              }}
              onUserAssign={async (userId, deptId, role) => {
                try {
                  if (role === 'remove') {
                    await axios.delete(`hr-user-departments/${userId}/${deptId}`);
                  } else {
                    await axios.post('hr-user-departments/add', { userId, departmentId: deptId, role });
                  }
                  loadData();
                } catch {}
              }}
              onDeptsChange={loadData}
            />
          )}
        </div>
      </TabMenu>
      <SidePanel open={showSettings} onClose={() => setShowSettings(false)} width={624}>
        <SettingsPanelBody><HrSettingsDrawer embedded /></SettingsPanelBody>
      </SidePanel>
    </div>
  );
};

export default HrPage;
